<template>
  <section class="conversation-gate">
    <strong>{{ gate.kind === 'media' ? '媒体生成与费用确认' : gate.kind === 'input' ? '团队需要你的补充' : gate.kind === 'artifact' ? '文档内容与成品确认' : '当前步骤确认' }}</strong>
    <p>{{ gate.question }}</p>
    <div v-if="gate.kind === 'media' && gate.media && gate.media.state !== 'selection'" class="media-quote">
      <b>{{ gate.media.modelName }} · {{ gate.media.points }} 点</b>
      <p v-if="gate.media.billingSeconds !== null">文字估算计费时长：{{ gate.media.billingSeconds }} 秒（沿用原数字人规则）</p>
      <p>{{ gate.media.input && (gate.media.input.prompt || gate.media.input.text) }}</p>
      <div v-if="gate.media.kind === 'digitalHuman'">
        <img v-if="gate.media.selected && gate.media.selected.coverUrl" :src="gate.media.selected.coverUrl" class="selected-avatar" alt="已确认的模特" />
        <p>模特：{{ gate.media.selected && gate.media.selected.personName || gate.media.input.personId }}；声音：{{ gate.media.selected && gate.media.selected.voiceName || gate.media.input.audioManId }}</p>
        <p v-if="gate.media.selected">背景：{{ gate.media.selected.backgroundMode === 'image' ? '已上传的背景图片' : gate.media.selected.backgroundMode === 'color' ? '纯色 ' + gate.media.selected.bgColor : '保留原形象背景' }}</p>
        <img v-if="gate.media.selected && gate.media.selected.backgroundMode === 'image'" :src="gate.media.selected.bgSrcUrl" class="selected-avatar" alt="已确认的视频背景" />
      </div>
      <small v-if="gate.media.orderNo">原订单：{{ gate.media.orderNo }}，无需重复提交</small>
      <small v-if="typeof gate.media.chargedPoints === 'number'">原单实扣 {{ gate.media.chargedPoints }} 点；已退 {{ gate.media.refundedPoints || 0 }} 点</small>
      <small v-if="gate.media.state === 'quoted'">确认后按现有价格生成；如需修改内容，请拒绝后在主管对话中提出。报价15分钟内有效。</small>
      <small v-else-if="gate.media.state === 'unknown'">暂无法确认厂商接单结果，保留原操作等待核查，请勿重复新建。</small>
      <small v-else-if="gate.media.state === 'declined'">你已拒绝本次生成，不会发起新订单。</small>
      <small v-else-if="gate.media.state === 'failed'">{{ gate.media.failureCode === 'project_media_vendor_balance_insufficient' ? '数字人厂商余额不足，请联系管理员补充蝉镜余额，实际退点见原订单。' : '生成未完成，请核对原订单的实际扣退记录。' }}</small>
      <small v-else-if="gate.media.state === 'completed'">媒体已保存到项目，可以预览和下载。</small>
      <small v-else>后台继续查询原订单，完成后成果会回到主对话。</small>
    </div>
    <template v-if="actionable">
      <agent-artifact-panel v-if="gate.kind === 'artifact'" :session-guid="message.taskGuid" runtime-engine="mastra" entry-mode="project" @jobs-change="$emit('changed')" />
      <template v-else-if="gate.kind === 'media' && gate.media.state === 'selection'">
        <project-digital-human-selection :key="gate.id" :media="gate.media" :busy="busy" @configure="configure" />
        <el-button size="small" :disabled="busy" @click="decide(false)">取消本次生成</el-button>
      </template>
      <template v-else>
        <el-input v-if="gate.kind === 'input'" v-model="answer" type="textarea" :rows="3" placeholder="填写后继续后台工作" maxlength="20000" />
        <el-button type="primary" size="small" :loading="busy" :disabled="gate.kind === 'input' && !answer.trim()" @click="decide(true)">{{ gate.kind === 'media' ? `确认生成（${gate.media.points}点）` : gate.kind === 'input' ? '补充并继续' : '确认并继续' }}</el-button>
        <el-button v-if="gate.kind !== 'input'" size="small" :disabled="busy" @click="decide(false)">拒绝</el-button>
      </template>
    </template>
    <small v-else-if="decisionHint">{{ decisionHint }}</small>
  </section>
</template>
<script>
import { decideProjectApproval, provideProjectInput, configureProjectMedia } from '@/apis/project-team'
import { projectGateActionable } from '@/utils/project-conversation-state.mjs'
import AgentArtifactPanel from '@/views/app/layout-agent/components/agent-artifact-panel.vue'
import ProjectDigitalHumanSelection from './project-digital-human-selection.vue'
export default {
  components: { AgentArtifactPanel, ProjectDigitalHumanSelection }, props: { projectGuid: String, message: Object },
  data: () => ({ answer: '', busy: false }),
  computed: {
    gate() { return this.message.payload.gate || {} },
    actionable() { return projectGateActionable(this.message.payload) },
    decisionHint() {
      if (this.gate.kind === 'media' && this.gate.media) {
        if (this.gate.media.state === 'declined') return '拒绝决定已保存'
        if (['completed', 'failed'].includes(this.gate.media.state)) return ''
      }
      return this.message.payload.status === 'decision_recorded' ? '你的决定已保存，后台将继续处理' : '此确认事项已处理或失效，以最新状态为准'
    }
  },
  methods: {
    async configure(mediaInput) {
      if (!this.actionable || this.busy || this.gate.media.state !== 'selection') return
      this.busy = true
      try {
        await configureProjectMedia({ projectGuid: this.projectGuid, runGuid: this.message.runGuid,
          gateId: this.gate.id, gateVersion: this.gate.version, mediaInput })
        this.$emit('changed')
      } catch (e) { this.$message.error(e.message); this.$emit('changed') } finally { this.busy = false }
    },
    async decide(approved) {
      if (!this.actionable || this.busy) return; this.busy = true
      try { const input = { projectGuid: this.projectGuid, runGuid: this.message.runGuid, gateId: this.gate.id, gateVersion: this.gate.version }
        await (this.gate.kind === 'input' ? provideProjectInput({ ...input, answer: this.answer.trim() }) : decideProjectApproval({ ...input, approved }))
        this.answer = ''; this.$emit('changed')
      } catch (e) { this.$message.error(e.message); this.$emit('changed') } finally { this.busy = false }
    }
  }
}
</script>
<style scoped>
.conversation-gate{margin-top:10px;background:#fffaf0;border:1px solid #ecd9b4;border-radius:12px;padding:20px}.conversation-gate p{white-space:pre-wrap;line-height:1.8}.conversation-gate .el-button{margin-top:12px}.conversation-gate small{color:#999}
.selected-avatar{width:90px;height:120px;object-fit:cover;border-radius:8px}
</style>
